import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Users,
  Target,
  TrendingUp,
  AlertCircle,
  Award,
  LayoutGrid,
  List,
  Plus,
  ArrowUpRight,
  Clock,
  CheckCircle2,
} from 'lucide-react';
import { ComingSoonBanner } from '../ComingSoonBanner';
import { appDate } from '../../../utils/appDateFormat';

interface Objective {
  id: string;
  focus: string;
  horizon: string;
  lastDiscussed: string;
  recentMilestone: string;
  status: string;
  linkedRole: string;
  creationDate: string;
  supporter: string;
}

interface TeamMember {
  id: string;
  name: string;
  position: string;
  activeObjectives: number;
  objectives: Objective[];
  needsAttention: boolean;
  lastCheckIn: string;
  attentionReason?: string;
}

const teamData = {
  overview: {
    totalMembers: 8,
    activeObjectives: 12,
    noObjectives: 2,
    needsAttention: 3,
    avgHorizon: '5.2 months',
    recentMilestones: 7,
  },
  teamMembers: [
    {
      id: 'emp_001',
      name: 'Sarah Johnson',
      position: 'Senior Product Designer',
      activeObjectives: 2,
      objectives: [
        {
          id: 'obj_001',
          focus: 'Advanced data visualization skills',
          horizon: '6 months',
          lastDiscussed: '2024-11-20T14:00:00Z',
          recentMilestone: 'Completed D3.js course',
          status: 'on-track',
          linkedRole: 'Senior Product Designer',
          creationDate: '2024-05-15T09:00:00Z',
          supporter: 'Michael Chen',
        },
        {
          id: 'obj_002',
          focus: 'Leadership in design systems',
          horizon: '3 months',
          lastDiscussed: '2024-11-15T10:00:00Z',
          recentMilestone: 'Led component library workshop',
          status: 'on-track',
          linkedRole: 'Senior Product Designer',
          creationDate: '2024-08-20T10:00:00Z',
          supporter: 'Michael Chen',
        },
      ],
      needsAttention: false,
      lastCheckIn: '2024-11-20T14:00:00Z',
    },
    {
      id: 'emp_002',
      name: 'Ahmed Al-Rashid',
      position: 'Software Developer',
      activeObjectives: 1,
      objectives: [
        {
          id: 'obj_003',
          focus: 'Backend architecture patterns',
          horizon: '6 months',
          lastDiscussed: '2024-09-10T09:00:00Z',
          recentMilestone: 'Database optimization project',
          status: 'needs-attention',
          linkedRole: 'Software Developer',
          creationDate: '2024-04-10T08:00:00Z',
          supporter: 'Michael Chen',
        },
      ],
      needsAttention: true,
      lastCheckIn: '2024-09-10T09:00:00Z',
      attentionReason: 'Not discussed in 10 weeks',
    },
    {
      id: 'emp_003',
      name: 'Emily Chen',
      position: 'Marketing Specialist',
      activeObjectives: 0,
      objectives: [],
      needsAttention: true,
      lastCheckIn: '2024-11-18T16:00:00Z',
      attentionReason: 'No active development objectives',
    },
    {
      id: 'emp_004',
      name: 'David Thompson',
      position: 'Product Manager',
      activeObjectives: 2,
      objectives: [
        {
          id: 'obj_004',
          focus: 'Strategic product planning',
          horizon: '6 months',
          lastDiscussed: '2024-11-22T11:00:00Z',
          recentMilestone: 'Completed roadmap workshop',
          status: 'on-track',
          linkedRole: 'Product Manager',
          creationDate: '2024-06-01T09:00:00Z',
          supporter: 'Michael Chen',
        },
        {
          id: 'obj_005',
          focus: 'Stakeholder management',
          horizon: '3 months',
          lastDiscussed: '2024-11-22T11:00:00Z',
          recentMilestone: 'Led executive presentation',
          status: 'on-track',
          linkedRole: 'Product Manager',
          creationDate: '2024-09-10T14:00:00Z',
          supporter: 'Michael Chen',
        },
      ],
      needsAttention: false,
      lastCheckIn: '2024-11-22T11:00:00Z',
    },
    {
      id: 'emp_005',
      name: 'Jessica Lee',
      position: 'UX Researcher',
      activeObjectives: 1,
      objectives: [
        {
          id: 'obj_006',
          focus: 'Quantitative research methods',
          horizon: '6 months',
          lastDiscussed: '2024-10-05T13:00:00Z',
          recentMilestone: 'Statistics course enrollment',
          status: 'needs-attention',
          linkedRole: 'UX Researcher',
          creationDate: '2024-05-20T11:00:00Z',
          supporter: 'Michael Chen',
        },
      ],
      needsAttention: true,
      lastCheckIn: '2024-10-05T13:00:00Z',
      attentionReason: 'Not discussed in 7 weeks',
    },
    {
      id: 'emp_006',
      name: 'Omar Hassan',
      position: 'Frontend Developer',
      activeObjectives: 0,
      objectives: [],
      needsAttention: true,
      lastCheckIn: '2024-11-25T10:00:00Z',
      attentionReason: 'No active development objectives',
    },
  ] as TeamMember[],
};

export default function TeamDevelopmentDashboard() {
  const { t } = useTranslation();
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState<'card' | 'table'>('card');

  const filteredMembers = teamData.teamMembers.filter((member) => {
    if (filterStatus === 'needs-attention') return member.needsAttention;
    if (filterStatus === 'no-objectives') return member.activeObjectives === 0;
    if (filterStatus === 'active') return member.activeObjectives > 0 && !member.needsAttention;
    return true;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'on-track':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'needs-attention':
        return 'text-amber-600 bg-amber-50 border-amber-200';
      default:
        return 'text-gray-600 bg-muted border-border';
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <ComingSoonBanner
        icon={<LayoutDashboard className="w-6 h-6" />}
        title="Team Development Dashboard"
        description="Team Development Dashboard is currently in development. This is a preview of the upcoming feature."
        gradientFrom="from-teal-500"
        gradientTo="to-cyan-500"
      />

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">{t('Team Development Overview')}</h1>
        <p className="text-gray-600 mt-2">
          {t('Monitor team development engagement and provide timely support')}
        </p>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <div className="bg-card rounded-xl shadow-sm border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-blue-500" />
            <span className="text-sm text-gray-500">{t('Total Members')}</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{teamData.overview.totalMembers}</p>
        </div>
        <div className="bg-card rounded-xl shadow-sm border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-5 h-5 text-green-500" />
            <span className="text-sm text-gray-500">{t('Active Objectives')}</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{teamData.overview.activeObjectives}</p>
        </div>
        <div className="bg-card rounded-xl shadow-sm border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-sm text-gray-500">{t('No Objectives')}</span>
          </div>
          <p className="text-2xl font-bold text-red-600">{teamData.overview.noObjectives}</p>
        </div>
        <div className="bg-card rounded-xl shadow-sm border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-amber-500" />
            <span className="text-sm text-gray-500">{t('Needs Attention')}</span>
          </div>
          <p className="text-2xl font-bold text-amber-600">{teamData.overview.needsAttention}</p>
        </div>
        <div className="bg-card rounded-xl shadow-sm border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-indigo-500" />
            <span className="text-sm text-gray-500">{t('Avg Horizon')}</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{teamData.overview.avgHorizon}</p>
        </div>
        <div className="bg-card rounded-xl shadow-sm border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <Award className="w-5 h-5 text-purple-500" />
            <span className="text-sm text-gray-500">{t('Recent Milestones')}</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{teamData.overview.recentMilestones}</p>
        </div>
      </div>

      {/* Development Insights */}
      <div className="bg-card rounded-xl shadow-sm border border-border p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-teal-500" />
          {t('Development Insights')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <p className="text-sm font-medium text-green-800">{t('On Track')}</p>
            <p className="text-xl font-bold text-green-900 mt-1">4 {t('objectives')}</p>
            <p className="text-xs text-green-600 mt-1">{t('Progressing as expected')}</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
            <p className="text-sm font-medium text-amber-800">{t('Needs Discussion')}</p>
            <p className="text-xl font-bold text-amber-900 mt-1">2 {t('objectives')}</p>
            <p className="text-xs text-amber-600 mt-1">{t('Not discussed recently')}</p>
          </div>
          <div className="bg-red-50 rounded-lg p-4 border border-red-200">
            <p className="text-sm font-medium text-red-800">{t('No Objectives')}</p>
            <p className="text-xl font-bold text-red-900 mt-1">2 {t('members')}</p>
            <p className="text-xs text-red-600 mt-1">{t('Need development plans')}</p>
          </div>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterStatus === 'all'
                ? 'bg-teal-600 text-white'
                : 'bg-card border border-border text-gray-700 hover:bg-gray-50'
            }`}
          >
            {t('All Team')} ({teamData.teamMembers.length})
          </button>
          <button
            onClick={() => setFilterStatus('needs-attention')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterStatus === 'needs-attention'
                ? 'bg-amber-500 text-white'
                : 'bg-card border border-border text-gray-700 hover:bg-gray-50'
            }`}
          >
            {t('Needs Attention')} ({teamData.overview.needsAttention})
          </button>
          <button
            onClick={() => setFilterStatus('no-objectives')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterStatus === 'no-objectives'
                ? 'bg-red-500 text-white'
                : 'bg-card border border-border text-gray-700 hover:bg-gray-50'
            }`}
          >
            {t('No Objectives')} ({teamData.overview.noObjectives})
          </button>
          <button
            onClick={() => setFilterStatus('active')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterStatus === 'active'
                ? 'bg-green-500 text-white'
                : 'bg-card border border-border text-gray-700 hover:bg-gray-50'
            }`}
          >
            {t('Active Development')}
          </button>
        </div>

        <div className="flex items-center space-x-3">
          {/* View Toggle */}
          <div className="flex items-center bg-card border border-border rounded-lg p-1">
            <button
              onClick={() => setViewMode('card')}
              className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'card'
                  ? 'bg-teal-600 text-white'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <LayoutGrid className="w-4 h-4" />
              <span>{t('Card')}</span>
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'table'
                  ? 'bg-teal-600 text-white'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <List className="w-4 h-4" />
              <span>{t('Table')}</span>
            </button>
          </div>

          <button
           
            className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg"
           
          >
            <Plus className="w-4 h-4" />
            {t('Create Objective')}
          </button>
        </div>
      </div>

      {/* Team Members - Card View */}
      {viewMode === 'card' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredMembers.map((member) => (
            <div
              key={member.id}
              className="bg-card rounded-xl shadow-sm border border-border p-5"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white font-semibold">
                    {member.name.charAt(0)}
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">{member.name}</h3>
                    <p className="text-xs text-gray-500">{member.position}</p>
                  </div>
                </div>
                {member.needsAttention && (
                  <span className="px-2 py-1 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-full">
                    {t('Attention')}
                  </span>
                )}
              </div>

              {member.attentionReason && (
                <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-amber-50 rounded-lg border border-amber-200">
                  <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                  <p className="text-xs text-amber-700">{t(member.attentionReason)}</p>
                </div>
              )}

              {member.objectives.length > 0 ? (
                <div className="space-y-3">
                  {member.objectives.map((obj) => (
                    <div key={obj.id} className={`p-3 rounded-lg border ${getStatusColor(obj.status)}`}>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-medium">{obj.focus}</p>
                        <span className="text-xs capitalize">{obj.status.replace('-', ' ')}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs opacity-75">
                        <span>{t('Horizon')}: {obj.horizon}</span>
                        <span>{t('Milestone')}: {obj.recentMilestone}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-700">
                  <Target className="w-6 h-6 mx-auto mb-1" />
                  <p className="text-xs">{t('No active objectives')}</p>
                </div>
              )}

              <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
                <span className="text-xs text-gray-500">
                  {t('Last check-in')}: {appDate(member.lastCheckIn)}
                </span>
                <button
                 
                  className="flex items-center gap-1 text-xs text-teal-400"
                 
                >
                  {t('View Details')} <ArrowUpRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Table View */
        <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('Team Member')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('Position')}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('Objectives')}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('Status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('Last Check-in')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('Actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredMembers.map((member) => (
                <tr key={member.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white text-sm font-semibold">
                        {member.name.charAt(0)}
                      </div>
                      <span className="text-sm font-medium text-gray-900">{member.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {member.position}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium text-gray-900">
                    {member.activeObjectives}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {member.needsAttention ? (
                      <span className="px-2 py-1 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-full">
                        {t('Attention')}
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-medium bg-green-50 text-green-700 border border-green-200 rounded-full">
                        <CheckCircle2 className="w-3 h-3 inline mr-1" />
                        {t('On Track')}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {appDate(member.lastCheckIn)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button
                     
                      className="text-xs text-teal-400"
                     
                    >
                      {t('Review')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filteredMembers.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-900 font-medium mb-2">{t('No team members match this filter')}</p>
          <p className="text-sm text-gray-500">{t('Try adjusting your filter selection')}</p>
        </div>
      )}
    </div>
  );
}
